import React from 'react';
import { List, Tag, Button, Space, Typography, Spin, Alert } from 'antd';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getUnifiedTasks, updateUnifiedTask, type UnifiedTaskRow } from '@/services/unifiedTask.service';

const { Text } = Typography;

const priorityColor: Record<string, string> = {
  Critical: 'red',
  High: 'orange',
  Medium: 'gold',
  Low: 'default',
};

export const MyTasksZone: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: tasks = [], isLoading, isError, error } = useQuery({
    queryKey: ['control-tower', 'my-tasks'],
    queryFn: () =>
      getUnifiedTasks({
        module: 'Sales',
        assigned_to: 'me',
        open_only: true,
        sync: true,
        limit: 20,
      }),
  });

  const completeMut = useMutation({
    mutationFn: (id: string) => updateUnifiedTask(id, { status: 'Completed' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['control-tower', 'my-tasks'] }),
  });

  if (isLoading) {
    return (
      <ControlTowerSectionCard zone="myTasks" title="My tasks">
        <Spin />
      </ControlTowerSectionCard>
    );
  }
  if (isError) {
    return (
      <ControlTowerSectionCard zone="myTasks" title="My tasks">
        <Alert type="warning" message={(error as Error)?.message ?? 'Could not load tasks'} />
      </ControlTowerSectionCard>
    );
  }

  return (
    <ControlTowerSectionCard
      zone="myTasks"
      title={`My tasks${tasks.length ? ` (${tasks.length})` : ''}`}
      extra={
        tasks.length > 0 ? (
          <Button type="link" size="small" onClick={() => navigate('/unified')}>
            Open task board
          </Button>
        ) : null
      }
    >
      {tasks.length === 0 ? (
        <Text type="secondary">No open sales tasks assigned to you.</Text>
      ) : (
        <List<UnifiedTaskRow>
          size="small"
          dataSource={tasks}
          renderItem={(t) => (
            <List.Item
              actions={[
                t.action_link ? (
                  <Button key="view" type="link" size="small" onClick={() => navigate(t.action_link!)}>
                    View
                  </Button>
                ) : null,
                <Button
                  key="done"
                  type="link"
                  size="small"
                  loading={completeMut.isPending}
                  onClick={() => completeMut.mutate(t.id)}
                >
                  Done
                </Button>,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <Tag color={priorityColor[t.priority] ?? 'default'}>{t.priority}</Tag>
                    <span>{t.source_document_number || t.recommended_action}</span>
                  </Space>
                }
                description={
                  <>
                    {t.party_display_name && <div>{t.party_display_name}</div>}
                    <Text type="secondary">{t.recommended_action}</Text>
                    {t.due_date && (
                      <div>
                        <Text type="secondary">Due: {String(t.due_date).slice(0, 10)}</Text>
                      </div>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </ControlTowerSectionCard>
  );
};
